/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import {
  type ChatStreamDone,
  type ChatStreamMeta,
  type ChatStreamRequest,
  type ChatStreamToolResult,
  type ChatStreamToolUse,
  streamChatMessage,
} from "@/lib/api/chat-stream";
import {
  adminActivityActions,
  adminActivityAnalytics,
  adminDeleteUser,
  adminFeedbackAnalytics,
  adminGetUser,
  adminListActivity,
  adminListAnswerFeedback,
  adminListFeedback,
  adminListUsers,
  adminUpdateUser,
  type ChatConversationDetail,
  type ChatConversationSummary,
  type ChatMessage,
  createChatConversation,
  type CreateConversationRequest,
  deleteChatConversation,
  getActivity,
  getChatConversation,
  getHealth,
  getMe,
  getMeDetail,
  listChatConversations,
  logout,
  type PartSuggestion,
  submitSuggestionFeedback,
  updateProfile,
} from "@/lib/api/generated";
import { logger } from "@/lib/logger";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Admin feedback ──────────────────────────────────────────────────────

export interface AdminFeedbackParams {
  isCorrect?: boolean;
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminFeedback(params: AdminFeedbackParams = {}) {
  return useQuery({
    queryKey: ["admin", "feedback", params],
    queryFn: async () => {
      const { data } = await adminListFeedback({
        query: {
          is_correct: params.isCorrect,
          search: params.search,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export interface AdminAnswerFeedbackParams {
  isHelpful?: boolean;
  search?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

/** Teams' whole-answer ratings ("was this helpful?"), ordered by `updated_at`. */
export function useAdminAnswerFeedback(params: AdminAnswerFeedbackParams = {}) {
  return useQuery({
    queryKey: ["admin", "feedback", "answers", params],
    queryFn: async () => {
      const { data } = await adminListAnswerFeedback({
        query: {
          is_helpful: params.isHelpful,
          search: params.search,
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    placeholderData: (prev) => prev,
  });
}

export function useAdminFeedbackAnalytics(days = 90) {
  return useQuery({
    queryKey: ["admin", "feedback", "analytics", days],
    queryFn: async () => {
      const { data } = await adminFeedbackAnalytics({ query: { days } });
      return data;
    },
  });
}

// ─── Chat ────────────────────────────────────────────────────────────────

/** List the current user's chat conversations (sidebar). */
export function useChatConversations() {
  return useQuery({
    queryKey: ["chat", "conversations"],
    queryFn: async () => {
      const { data } = await listChatConversations();
      return data ?? [];
    },
  });
}

/** Full detail (messages) for a single conversation. `null` id disables the query. */
export function useChatConversation(conversationId: string | null) {
  return useQuery({
    queryKey: ["chat", "conversations", conversationId],
    queryFn: async () => {
      if (conversationId === null) {
        return null;
      }
      const { data } = await getChatConversation({
        path: { conversation_id: conversationId },
      });
      return data;
    },
    enabled: conversationId !== null,
    // A 404 (missing / not owned) is definitive — don't retry it, so the page
    // can redirect to `/chat` promptly instead of waiting out the default
    // retries. Other failures keep the global retry policy.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404 || status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * The brand a conversation is pinned to. Required and immutable server-side:
 * it scopes every machine / BOM lookup the conversation performs, so it has to
 * be chosen before the conversation exists (see `features/select-brand`).
 */
export type ConversationBrand = CreateConversationRequest["brand"];

export function useCreateChatConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { brand: ConversationBrand; title?: string }) => {
      const { data } = await createChatConversation({
        body: { brand: vars.brand, title: vars.title },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

export function useDeleteChatConversation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { conversationId: string }) => {
      const { data } = await deleteChatConversation({
        path: { conversation_id: vars.conversationId },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });
}

/**
 * One entry in a turn's chronological transcript. The backend interleaves prose
 * and tool activity in arrival order (text deltas, then a tool call, its
 * result, then the next iteration's prose, …), so the turn is a single ordered
 * list rather than a text blob plus a separate tool log:
 *   - `text` — a contiguous run of assistant prose (markdown). A fresh block is
 *     started whenever prose resumes after a tool call.
 *   - `tool` — one retrieval call, keyed by its `id`: `running` from `tool_use`
 *     until the matching `tool_result` flips it to `done` (with `ok`).
 */
export type ChatTurnItem =
  | { kind: "text"; text: string }
  | { kind: "tool"; id: string; name: string; status: "running" | "done"; ok?: boolean };

/** Live state of an in-progress streamed assistant turn. */
export interface ChatStreamState {
  /**
   * The conversation this stream belongs to, set the moment `send()` fires.
   * There is one global stream at a time, so the UI uses this to scope the
   * live transcript to the active chat — switching to another conversation
   * mid-stream must not surface this stream's activity under the wrong chat.
   * `null` only in the idle state.
   */
  conversationId: string | null;
  /** The persisted user message from the `meta` event, shown immediately. */
  userMessage: ChatMessage | null;
  /** The turn transcript so far, in chronological arrival order. */
  items: ChatTurnItem[];
  /** True from send until the stream fully completes or errors. */
  isStreaming: boolean;
  /** Error detail from an `error` event or transport failure, else null. */
  error: string | null;
}

export const IDLE_STREAM_STATE: ChatStreamState = {
  conversationId: null,
  userMessage: null,
  items: [],
  isStreaming: false,
  error: null,
};

/**
 * Completed turn transcripts, keyed by the persisted assistant message id.
 * Populated on `done` and kept for the rest of the session so the interleaved
 * trace stays visible after the turn ends (a reload — which loses this map —
 * collapses the message back to its plain final answer).
 */
export type ChatTurnTraces = Record<string, ChatTurnItem[]>;

/**
 * Streamed replacement for {@link useSendChatMessage}.
 *
 * The send endpoint responds with `text/event-stream`, which openapi-ts cannot
 * model — so this hook drives the hand-written {@link streamChatMessage} reader
 * and mirrors its events into an ordered {@link ChatStreamState.items}
 * transcript:
 *   - `meta`        → `userMessage` (the technician's turn, shown at once)
 *   - `text`        → extends the trailing `text` item, or starts a new one if
 *                     the last item was a tool row (interleaving)
 *   - `tool_use`    → pushes a `running` `tool` item (deduped by id)
 *   - `tool_result` → flips the matching `tool` item to `done` (kept in place)
 *   - `done`        → merges the authoritative persisted messages into the
 *                     conversation-detail + list caches, exactly as
 *                     `useSendChatMessage` did (so `needs_machine_selection`,
 *                     which is live-only and reads back null on refetch,
 *                     survives), STORES the completed transcript under the
 *                     assistant message id (session-only), then resets state.
 *
 * `send()` starts a turn; `isStreaming` gates the composer. `traces` holds the
 * completed transcript for each assistant message id this session. The
 * auto-create-conversation and machine-picker-resend flows are unchanged —
 * they call `send()` with the same shape the mutation took.
 */
export function useStreamChatMessage() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ChatStreamState>(IDLE_STREAM_STATE);
  const [traces, setTraces] = useState<ChatTurnTraces>({});
  const abortRef = useRef<AbortController | null>(null);
  // Tracks the persisted user message across the stream's async callbacks —
  // `onDone` needs it, and reading it off React state would capture a stale
  // render-time snapshot rather than the value `meta` just set.
  const userMessageRef = useRef<ChatMessage | null>(null);
  // Same rationale for the transcript: `onDone` needs the final `items` to
  // store as the trace, but its closure would capture a stale snapshot. Keep a
  // ref in lock-step with the state updates below.
  const itemsRef = useRef<ChatTurnItem[]>([]);

  // Merge the persisted user turn (from the `meta` event) into the detail
  // cache. Used by the error paths: `onDone` merges user + assistant together,
  // but if the stream fails after `meta` the assistant reply never lands, so we
  // still persist the user turn here — otherwise the next `send()` resets the
  // live state and the bubble vanishes even though the DB has it. De-duped by
  // message id so a later refetch (or a retry that replays `meta`) can't insert
  // it twice.
  const persistUserMessage = useCallback(
    (conversationId: string) => {
      const userMessage = userMessageRef.current;
      if (!userMessage) {
        return;
      }
      queryClient.setQueryData<ChatConversationDetail | null>(
        ["chat", "conversations", conversationId],
        (prev) => {
          if (!prev || prev.messages.some((m) => m.id === userMessage.id)) {
            return prev;
          }
          return { ...prev, messages: [...prev.messages, userMessage] };
        },
      );
    },
    [queryClient],
  );

  // Update the transcript, keeping `itemsRef` in step so `onDone` can read the
  // final list without a stale closure.
  const applyItems = useCallback(
    (next: (prev: ChatTurnItem[]) => ChatTurnItem[]) => {
      setState((prev) => {
        const items = next(prev.items);
        itemsRef.current = items;
        return { ...prev, items };
      });
    },
    [setState],
  );

  const send = useCallback(
    async (vars: ChatStreamRequest): Promise<void> => {
      // Abort any previous in-flight stream before starting a new one.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      userMessageRef.current = null;
      itemsRef.current = [];
      setState({ ...IDLE_STREAM_STATE, isStreaming: true, conversationId: vars.conversationId });

      try {
        await streamChatMessage(
          vars,
          {
            onMeta: (meta: ChatStreamMeta) => {
              userMessageRef.current = meta.user_message;
              setState((prev) => ({ ...prev, userMessage: meta.user_message }));
            },
            onText: (delta: string) => {
              // Extend the trailing prose block, or start a fresh one if the
              // last item was a tool row (so prose resuming after a tool call
              // reads as its own paragraph in the transcript).
              applyItems((items) => {
                const last = items.at(-1);
                if (last?.kind === "text") {
                  return [...items.slice(0, -1), { kind: "text", text: last.text + delta }];
                }
                return [...items, { kind: "text", text: delta }];
              });
            },
            onToolUse: (tool: ChatStreamToolUse) => {
              logger.debug("[chat]", "tool_use", tool.name, new Date().toISOString());
              // Push a running tool row. Guard against a duplicate id (a
              // reconnect/replay) so we don't stack two rows for one call.
              applyItems((items) =>
                items.some((item) => item.kind === "tool" && item.id === tool.id)
                  ? items
                  : [...items, { kind: "tool", id: tool.id, name: tool.name, status: "running" }],
              );
            },
            onToolResult: (tool: ChatStreamToolResult) => {
              logger.debug("[chat]", "tool_result", tool.name, tool.ok, new Date().toISOString());
              // Flip the matching tool row to done (kept in place). If no
              // `tool_use` was seen (shouldn't happen), append it as done.
              applyItems((items) =>
                items.some((item) => item.kind === "tool" && item.id === tool.id)
                  ? items.map((item) =>
                      item.kind === "tool" && item.id === tool.id
                        ? { ...item, status: "done", ok: tool.ok }
                        : item,
                    )
                  : [
                      ...items,
                      { kind: "tool", id: tool.id, name: tool.name, status: "done", ok: tool.ok },
                    ],
              );
            },
            onDone: (done: ChatStreamDone) => {
              // Merge the authoritative persisted messages straight into the
              // detail cache rather than refetching — mirrors the old mutation.
              // `needs_machine_selection` is live-only (not a persisted column,
              // so a refetch reads it back as null); merging keeps the machine
              // picker on screen until the user clicks it.
              queryClient.setQueryData<ChatConversationDetail | null>(
                ["chat", "conversations", done.conversation.id],
                (prev) => {
                  if (!prev) {
                    return prev;
                  }
                  const withUser = userMessageRef.current
                    ? [...prev.messages, userMessageRef.current]
                    : prev.messages;
                  return {
                    ...prev,
                    title: done.conversation.title,
                    machine: done.conversation.machine,
                    updated_at: done.conversation.updated_at,
                    messages: [...withUser, done.assistant_message],
                  };
                },
              );
              // Keep the sidebar summary current too.
              queryClient.setQueryData<ChatConversationSummary[]>(
                ["chat", "conversations"],
                (prev) =>
                  prev?.map((c) => (c.id === done.conversation.id ? done.conversation : c)) ?? prev,
              );
              // Store the completed transcript under the assistant message id so
              // the interleaved trace stays visible for the rest of the session
              // (survives the state reset below; lost on reload → collapses to
              // the persisted final answer).
              if (itemsRef.current.length > 0) {
                const trace = itemsRef.current;
                setTraces((prev) => ({ ...prev, [done.assistant_message.id]: trace }));
              }
              setState(IDLE_STREAM_STATE);
              itemsRef.current = [];
              // Pick up a conversation not yet in the sidebar cache (e.g.
              // auto-created on first send).
              void queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
            },
            onError: (detail: string) => {
              logger.error("[chat]", "stream error", detail);
              // The stream failed before `done`, so the user turn was never
              // merged — persist it now so it survives the next send's reset.
              persistUserMessage(vars.conversationId);
              setState((prev) => ({ ...prev, isStreaming: false, error: detail }));
            },
          },
          controller.signal,
        );
      } catch (error) {
        // A backend `error` event already set state via onError; only surface
        // transport/HTTP failures (and ignore user-initiated aborts).
        if (controller.signal.aborted) {
          return;
        }
        // Transport/HTTP failure after `meta` — same as onError: keep the
        // already-persisted user turn from vanishing on the next send.
        persistUserMessage(vars.conversationId);
        setState((prev) =>
          prev.error
            ? prev
            : {
                ...prev,
                isStreaming: false,
                error: error instanceof Error ? error.message : "Something went wrong.",
              },
        );
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [queryClient, persistUserMessage, applyItems],
  );

  return { send, state, traces } as const;
}

/**
 * Mark an individual part suggestion as correct / incorrect.
 *
 * A single AI message may resolve several requested parts, each with a top pick
 * and alternatives — every one of those suggestions can be marked independently.
 * A suggestion is identified by the `message_id` plus its `item_id` +
 * `requested_item` pair (the same `item_id` may legitimately appear under two
 * different requested parts, so both are required to target the right row).
 */
export function useSubmitSuggestionFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      conversationId: string;
      messageId: string;
      itemId: string;
      requestedItem: string;
      isCorrect: boolean;
    }) => {
      await submitSuggestionFeedback({
        path: { message_id: vars.messageId },
        body: {
          item_id: vars.itemId,
          requested_item: vars.requestedItem,
          is_correct: vars.isCorrect,
        },
      });
      return vars;
    },
    onSuccess: (vars) => {
      // Feedback is a tiny per-suggestion toggle — merge the new value straight
      // into the conversation-detail cache instead of invalidating + refetching
      // the whole conversation (a refetch here would be wasteful and, as in
      // useSendChatMessage, this feature has been bitten by over-invalidation).
      // The suggestion lives inside one message inside the conversation's
      // `messages` array; update just that one suggestion's `feedback` field.
      queryClient.setQueryData<ChatConversationDetail | null>(
        ["chat", "conversations", vars.conversationId],
        (prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            messages: prev.messages.map((message: ChatMessage) => {
              if (message.id !== vars.messageId || !message.suggestions) {
                return message;
              }
              return {
                ...message,
                suggestions: message.suggestions.map(
                  (suggestion: PartSuggestion): PartSuggestion =>
                    suggestion.item_id === vars.itemId &&
                    suggestion.requested_item === vars.requestedItem
                      ? { ...suggestion, feedback: vars.isCorrect }
                      : suggestion,
                ),
              };
            }),
          };
        },
      );
    },
  });
}
